/**
 * Work Item Detail — the collaboration surface.
 *
 * Route: /projects/:engagementId/w/:workItemId
 *
 * Sections:
 *   - Header + state pill
 *   - State transition buttons (subject to two-hands + owner-not-self)
 *   - Contributors + Add contributor
 *   - Delegation chain + Delegate
 *   - Ownership transfer (initiate / accept / decline / force)
 *   - Work-item thread
 *
 * Zero financial fields displayed / accepted / stored.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CaretLeft, Plus, ArrowRight, X, ShieldCheck } from "@phosphor-icons/react";

import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import PageHeader from "../../components/layout/PageHeader";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
    Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { toast } from "sonner";
import ThreadPanel from "./components/ThreadPanel";
import AccountabilityChainPanel from "./components/AccountabilityChainPanel";
import {
    formatDate, formatUserName, StateBadge, PriorityBadge,
    PageLoading, PageError,
} from "./components/ui";


const STATE_TARGETS = {
    created: ["in_progress", "cancelled"],
    in_progress: ["awaiting_review", "blocked", "cancelled"],
    awaiting_review: ["completed", "in_progress", "cancelled"],
    blocked: ["in_progress", "cancelled"],
    completed: [],
    cancelled: [],
};


export default function WorkItemDetail() {
    const { engagementId, workItemId } = useParams();
    const { user } = useAuth();
    const perms = useMemo(() => new Set(user?.permissions || []), [user]);
    const isSuper = user?.role === "super_admin";

    const [workItem, setWorkItem] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get(`/projects/work-item/${workItemId}`);
            setWorkItem(r.data.work_item);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [workItemId]);

    if (loading) return <PageLoading label="Loading work item…" testId="op-wi-loading" />;
    if (!workItem) return (
        <PageError
            title="Work item not found"
            message="This work item may have been removed or you no longer have access."
            backTo={engagementId ? `/projects/${engagementId}` : "/projects"}
            backLabel="Back to project"
            testId="op-wi-not-found"
        />
    );

    const isOwner = user?.id === workItem.primary_owner_user_id;
    const activeContributors = (workItem.contributors || []).filter((c) => !c.removed_at);
    const activeDelegations = (workItem.delegation_chain || []).filter((d) => !d.revoked_at);

    return (
        <div className="space-y-6" data-testid="op-wi-detail-page">
            <PageHeader
                eyebrow="Delivery / Projects"
                title={workItem.title}
                description={
                    <span className="font-mono text-xs text-slate-500">
                        {workItem.work_item_number} · Role: {workItem.role_label} · Nature: {workItem.nature_of_work}
                    </span>
                }
                actions={
                    <Link to={`/projects/${engagementId}`} data-testid="op-wi-back-btn">
                        <Button variant="outline"><CaretLeft className="w-4 h-4 mr-1" /> Engagement</Button>
                    </Link>
                }
            />

            <section className="space-y-3">
                <div className="flex items-center gap-2 flex-wrap">
                    <StateBadge state={workItem.state} testId={`op-wi-state-${workItem.state}`} />
                    <PriorityBadge priority={workItem.priority} testId="op-wi-priority" />
                </div>
                <div className="text-sm text-slate-700 whitespace-pre-wrap" data-testid="op-wi-description">
                    {workItem.description}
                </div>
                <div className="text-xs text-slate-500 flex flex-wrap gap-x-4 gap-y-1">
                    <span data-testid="op-wi-owner-line">
                        Primary Owner:{" "}
                        <span className="text-slate-700">
                            {formatUserName(workItem.primary_owner_snapshot, workItem.primary_owner_user_id)}
                        </span>
                    </span>
                    {workItem.target_date && (
                        <span data-testid="op-wi-target-line">
                            Target: <span className="text-slate-700">{formatDate(workItem.target_date)}</span>
                        </span>
                    )}
                    {workItem.role_label && (
                        <span>Role: <span className="text-slate-700">{workItem.role_label}</span></span>
                    )}
                </div>
            </section>

            <StateControls workItem={workItem} onChanged={load} isOwner={isOwner} isSuper={isSuper} perms={perms} />

            <ContributorsPanel workItem={workItem} engagementId={engagementId} onChanged={load} perms={perms} isSuper={isSuper} />

            <DelegationPanel workItem={workItem} onChanged={load} perms={perms} isSuper={isSuper} />

            <OwnershipPanel workItem={workItem} engagementId={engagementId} onChanged={load} isOwner={isOwner} isSuper={isSuper} perms={perms} user={user} />

            <AccountabilityChainPanel workItemId={workItemId} />

            <ThreadPanel scope="work_item" engagementId={engagementId} workItemId={workItemId} />
        </div>
    );
}


// ─────────────────────────────────────────────────────────────────────
function StateControls({ workItem, onChanged, isOwner, isSuper, perms }) {
    const targets = STATE_TARGETS[workItem.state] || [];
    const [dialogState, setDialogState] = useState(null); // {to_state}
    const [reason, setReason] = useState("");
    const [submitting, setSubmitting] = useState(false);

    if (targets.length === 0) return null;

    const canDo = (to) => {
        if (to === "completed") {
            return !isOwner && (isSuper || perms.has("project:close"));
        }
        if (to === "cancelled") {
            return isSuper || isOwner || perms.has("project:cancel");
        }
        return true;
    };

    const submit = async () => {
        const need_reason = dialogState === "completed" || dialogState === "cancelled";
        if (need_reason && reason.trim().length < 3) {
            toast.error("A reason is required.");
            return;
        }
        setSubmitting(true);
        try {
            const key = `op-wi-state-${workItem.id}-${dialogState}-${crypto.randomUUID?.() || Date.now()}`;
            await api.post(
                `/projects/work-item/${workItem.id}/state`,
                { to_state: dialogState, reason: reason.trim() || undefined },
                { headers: { "If-Match": String(workItem._version), "Idempotency-Key": key } },
            );
            toast.success(`Moved to ${dialogState}.`);
            setDialogState(null); setReason("");
            onChanged?.();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section>
            <h3 className="text-sm font-semibold text-slate-700 mb-2">State transitions</h3>
            <div className="flex flex-wrap gap-2">
                {targets.map((to) => (
                    <Button
                        key={to}
                        variant={to === "cancelled" ? "destructive" : "outline"}
                        size="sm"
                        disabled={!canDo(to)}
                        onClick={() => setDialogState(to)}
                        data-testid={`op-wi-transition-${to}`}
                    >
                        → {to}
                    </Button>
                ))}
            </div>
            <Dialog open={!!dialogState} onOpenChange={(v) => (v ? null : setDialogState(null))}>
                <DialogContent data-testid="op-wi-transition-dialog">
                    <DialogHeader><DialogTitle>Move state → {dialogState}</DialogTitle></DialogHeader>
                    <div className="space-y-2 py-2">
                        {dialogState === "completed" && (
                            <div className="text-xs text-slate-500">
                                Two-hands rule: the Primary Owner cannot approve their own completion.
                                A reviewer must be a hierarchy senior or Engagement Coordinator (Super Admin fallback with mandatory review note).
                            </div>
                        )}
                        <Label>Reason / review note {["completed", "cancelled"].includes(dialogState) && <span className="text-rose-500">*</span>}</Label>
                        <Textarea data-testid="op-wi-transition-reason" value={reason} onChange={(e) => setReason(e.target.value)} />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialogState(null)}>Cancel</Button>
                        <Button onClick={submit} disabled={submitting} data-testid="op-wi-transition-submit">
                            {submitting ? "Applying…" : `Confirm ${(dialogState || "").replace(/_/g, " ")}`}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </section>
    );
}


// ─────────────────────────────────────────────────────────────────────
function ContributorsPanel({ workItem, engagementId, onChanged, perms, isSuper }) {
    const canAssign = isSuper || perms.has("project:assign");
    const [open, setOpen] = useState(false);
    const [candidates, setCandidates] = useState([]);
    const [userId, setUserId] = useState("");
    const [roleLabel, setRoleLabel] = useState("");
    const [saving, setSaving] = useState(false);
    const active = (workItem.contributors || []).filter((c) => !c.removed_at);

    useEffect(() => {
        if (!open) return;
        (async () => {
            try {
                const r = await api.get(`/projects/_meta/eligible-users?purpose=contributor&engagement_id=${engagementId}&limit=50`);
                setCandidates(r.data.items || []);
            } catch (err) {
                toast.error(formatApiError(err));
            }
        })();
    }, [open, engagementId]);

    const add = async () => {
        if (!userId) return toast.error("Pick a candidate.");
        setSaving(true);
        try {
            const key = `op-wi-contrib-add-${workItem.id}-${userId}-${crypto.randomUUID?.() || Date.now()}`;
            await api.post(
                `/projects/work-item/${workItem.id}/contributor`,
                { action: "add", user_id: userId, role_label: roleLabel || undefined },
                { headers: { "If-Match": String(workItem._version), "Idempotency-Key": key } },
            );
            toast.success("Contributor added.");
            setOpen(false); setUserId(""); setRoleLabel("");
            onChanged?.();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    const remove = async (c) => {
        const reason = window.prompt("Reason for removing contributor (audit-logged):");
        if (!reason || reason.trim().length < 3) return;
        try {
            const key = `op-wi-contrib-remove-${workItem.id}-${c.user_id}-${crypto.randomUUID?.() || Date.now()}`;
            await api.post(
                `/projects/work-item/${workItem.id}/contributor`,
                { action: "remove", user_id: c.user_id, reason_if_remove: reason.trim() },
                { headers: { "If-Match": String(workItem._version), "Idempotency-Key": key } },
            );
            onChanged?.();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    return (
        <section>
            <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold text-slate-700">Contributors ({active.length})</h3>
                {canAssign && (
                    <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid="op-add-contributor-btn">
                        <Plus className="w-4 h-4 mr-1" /> Add Contributor
                    </Button>
                )}
            </div>
            {active.length === 0 ? (
                <div className="text-sm text-slate-500" data-testid="op-contribs-empty">No contributors.</div>
            ) : (
                <ul className="divide-y divide-slate-100 border border-slate-200 rounded">
                    {active.map((c) => (
                        <li key={c.id} className="flex items-center justify-between px-3 py-2 text-sm" data-testid={`op-contrib-${c.user_id}`}>
                            <div>
                                <div className="font-medium text-slate-800">{c.user_snapshot?.name || c.user_id}</div>
                                <div className="text-xs text-slate-500">{c.role_label} · added {c.added_at?.substring(0, 10)}</div>
                            </div>
                            {canAssign && (
                                <button onClick={() => remove(c)} className="text-slate-400 hover:text-rose-600" data-testid={`op-contrib-remove-${c.user_id}`}>
                                    <X className="w-4 h-4" />
                                </button>
                            )}
                        </li>
                    ))}
                </ul>
            )}

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent data-testid="op-add-contributor-dialog">
                    <DialogHeader><DialogTitle>Add Contributor</DialogTitle></DialogHeader>
                    <div className="space-y-3 py-2">
                        <div>
                            <Label>User (eligible for this engagement)</Label>
                            <Select value={userId} onValueChange={setUserId}>
                                <SelectTrigger data-testid="op-contrib-user-trigger"><SelectValue placeholder="Choose..." /></SelectTrigger>
                                <SelectContent>
                                    {candidates.map((c) => (
                                        <SelectItem key={c.id} value={c.id}>{c.name || c.email}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>Role label (optional)</Label>
                            <Input data-testid="op-contrib-role" value={roleLabel} onChange={(e) => setRoleLabel(e.target.value)} placeholder="defaults to work-item role" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                        <Button onClick={add} disabled={saving} data-testid="op-contrib-submit">
                            {saving ? "Adding..." : "Add"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </section>
    );
}


// ─────────────────────────────────────────────────────────────────────
function DelegationPanel({ workItem, onChanged, perms, isSuper }) {
    const canDelegate = isSuper || perms.has("project:delegate");
    const [open, setOpen] = useState(false);
    const [candidates, setCandidates] = useState([]);
    const [toUserId, setToUserId] = useState("");
    const [reason, setReason] = useState("");
    const [saving, setSaving] = useState(false);
    const active = (workItem.delegation_chain || []).filter((d) => !d.revoked_at);

    useEffect(() => {
        if (!open) return;
        (async () => {
            try {
                const r = await api.get(`/projects/_meta/eligible-users?purpose=delegate&work_item_id=${workItem.id}&limit=50`);
                setCandidates(r.data.items || []);
            } catch (err) {
                toast.error(formatApiError(err));
            }
        })();
    }, [open, workItem.id]);

    const grant = async () => {
        if (!toUserId || !reason.trim()) return toast.error("Pick a user and reason.");
        setSaving(true);
        try {
            const key = `op-wi-delegate-${workItem.id}-${toUserId}-${crypto.randomUUID?.() || Date.now()}`;
            await api.post(
                `/projects/work-item/${workItem.id}/delegate`,
                { to_user_id: toUserId, reason: reason.trim() },
                { headers: { "If-Match": String(workItem._version), "Idempotency-Key": key } },
            );
            toast.success("Delegation granted.");
            setOpen(false); setToUserId(""); setReason("");
            onChanged?.();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    const revoke = async (d) => {
        const r = window.prompt("Reason for revocation (audit-logged):");
        if (!r || r.trim().length < 3) return;
        try {
            const key = `op-wi-delegate-revoke-${workItem.id}-${d.id}-${crypto.randomUUID?.() || Date.now()}`;
            await api.post(
                `/projects/work-item/${workItem.id}/delegate/${d.id}/revoke`,
                { reason: r.trim() },
                { headers: { "If-Match": String(workItem._version), "Idempotency-Key": key } },
            );
            onChanged?.();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    return (
        <section>
            <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold text-slate-700">Delegation chain ({active.length}/3)</h3>
                {canDelegate && active.length < 3 && (
                    <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid="op-delegate-btn">
                        <ArrowRight className="w-4 h-4 mr-1" /> Delegate
                    </Button>
                )}
            </div>
            {active.length === 0 ? (
                <div className="text-sm text-slate-500" data-testid="op-deleg-empty">Primary Owner is executing directly.</div>
            ) : (
                <ol className="divide-y divide-slate-100 border border-slate-200 rounded">
                    {active.map((d, idx) => (
                        <li key={d.id} className="flex items-center justify-between px-3 py-2 text-sm" data-testid={`op-deleg-${d.id}`}>
                            <div>
                                <div className="text-xs text-slate-500">
                                    Hop {d.depth} · from <span className="font-mono">{d.from_user_id}</span> → to <span className="font-mono">{d.to_user_id}</span>
                                </div>
                                <div className="text-xs text-slate-500">Reason: {d.reason}</div>
                            </div>
                            {canDelegate && (
                                <button onClick={() => revoke(d)} className="text-slate-400 hover:text-rose-600" data-testid={`op-deleg-revoke-${d.id}`}>
                                    Revoke
                                </button>
                            )}
                        </li>
                    ))}
                </ol>
            )}

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent>
                    <DialogHeader><DialogTitle>Delegate to</DialogTitle></DialogHeader>
                    <div className="space-y-3 py-2">
                        <div>
                            <Label>Eligible delegate (in your subtree, holds project:delegate)</Label>
                            <Select value={toUserId} onValueChange={setToUserId}>
                                <SelectTrigger data-testid="op-deleg-user-trigger"><SelectValue placeholder="Choose..." /></SelectTrigger>
                                <SelectContent>
                                    {candidates.map((c) => (
                                        <SelectItem key={c.id} value={c.id}>{c.name || c.email}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>Reason</Label>
                            <Textarea data-testid="op-deleg-reason" value={reason} onChange={(e) => setReason(e.target.value)} />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                        <Button onClick={grant} disabled={saving} data-testid="op-deleg-submit">
                            {saving ? "Granting..." : "Grant"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </section>
    );
}


// ─────────────────────────────────────────────────────────────────────
function OwnershipPanel({ workItem, engagementId, onChanged, isOwner, isSuper, perms, user }) {
    const pending = workItem.pending_ownership_transfer;
    const [initOpen, setInitOpen] = useState(false);
    const [candidates, setCandidates] = useState([]);
    const [proposedId, setProposedId] = useState("");
    const [reason, setReason] = useState("");
    const [saving, setSaving] = useState(false);
    const canInitiate = isSuper || isOwner || perms.has("project:assign");
    const canForce = isSuper || perms.has("project:admin");

    useEffect(() => {
        if (!initOpen) return;
        (async () => {
            try {
                const r = await api.get(`/projects/_meta/eligible-users?purpose=primary_owner&engagement_id=${engagementId}&limit=50`);
                setCandidates(r.data.items || []);
            } catch (err) {
                toast.error(formatApiError(err));
            }
        })();
    }, [initOpen, engagementId]);

    const initiate = async () => {
        if (!proposedId || !reason.trim()) return toast.error("Both fields required.");
        setSaving(true);
        try {
            const key = `op-wi-initiate-${workItem.id}-${crypto.randomUUID?.() || Date.now()}`;
            await api.post(
                `/projects/work-item/${workItem.id}/owner/initiate-transfer`,
                { proposed_owner_user_id: proposedId, reason: reason.trim() },
                { headers: { "If-Match": String(workItem._version), "Idempotency-Key": key } },
            );
            toast.success("Transfer initiated. Awaiting acceptance.");
            setInitOpen(false); setProposedId(""); setReason("");
            onChanged?.();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    const accept = async () => {
        try {
            const key = `op-wi-accept-${workItem.id}-${crypto.randomUUID?.() || Date.now()}`;
            await api.post(
                `/projects/work-item/${workItem.id}/owner/accept`,
                {},
                { headers: { "If-Match": String(workItem._version), "Idempotency-Key": key } },
            );
            toast.success("Ownership accepted.");
            onChanged?.();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const decline = async () => {
        const r = window.prompt("Reason for decline:");
        if (!r || r.trim().length < 3) return;
        try {
            const key = `op-wi-decline-${workItem.id}-${crypto.randomUUID?.() || Date.now()}`;
            await api.post(
                `/projects/work-item/${workItem.id}/owner/decline`,
                { reason: r.trim() },
                { headers: { "If-Match": String(workItem._version), "Idempotency-Key": key } },
            );
            toast.success("Ownership declined.");
            onChanged?.();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    return (
        <section>
            <h3 className="text-sm font-semibold text-slate-700 mb-2">Ownership</h3>
            <div className="text-sm text-slate-700 mb-2">
                Primary Owner: <span className="font-mono">{workItem.primary_owner_snapshot?.name || workItem.primary_owner_user_id}</span>
            </div>
            {pending && !pending.accepted_at && !pending.declined_at && (
                <div className="p-3 border border-amber-200 bg-amber-50 rounded" data-testid="op-pending-transfer">
                    <div className="text-sm text-amber-800">
                        <ShieldCheck className="inline w-4 h-4 mr-1" />
                        Ownership transfer pending — proposed to <span className="font-mono">{pending.proposed_owner_snapshot?.name || pending.proposed_owner_user_id}</span> · {pending.initiate_reason}
                    </div>
                    {pending.proposed_owner_user_id === user?.id && (
                        <div className="mt-2 flex gap-2">
                            <Button size="sm" onClick={accept} data-testid="op-transfer-accept-btn">Accept</Button>
                            <Button size="sm" variant="outline" onClick={decline} data-testid="op-transfer-decline-btn">Decline</Button>
                        </div>
                    )}
                </div>
            )}
            {!pending || pending.accepted_at || pending.declined_at ? (
                <div className="mt-2 flex flex-wrap gap-2">
                    {canInitiate && (
                        <Button size="sm" variant="outline" onClick={() => setInitOpen(true)} data-testid="op-transfer-initiate-btn">
                            Initiate transfer...
                        </Button>
                    )}
                </div>
            ) : null}

            <Dialog open={initOpen} onOpenChange={setInitOpen}>
                <DialogContent>
                    <DialogHeader><DialogTitle>Initiate ownership transfer</DialogTitle></DialogHeader>
                    <div className="space-y-3 py-2">
                        <div>
                            <Label>Proposed owner</Label>
                            <Select value={proposedId} onValueChange={setProposedId}>
                                <SelectTrigger data-testid="op-transfer-user-trigger"><SelectValue placeholder="Choose..." /></SelectTrigger>
                                <SelectContent>
                                    {candidates.map((c) => (
                                        <SelectItem key={c.id} value={c.id}>{c.name || c.email}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>Reason</Label>
                            <Textarea data-testid="op-transfer-reason" value={reason} onChange={(e) => setReason(e.target.value)} />
                        </div>
                        <div className="text-xs text-slate-500">
                            Two-step: the proposed owner must accept before ownership swaps.
                            The current Primary Owner remains accountable until then.
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setInitOpen(false)}>Cancel</Button>
                        <Button onClick={initiate} disabled={saving} data-testid="op-transfer-submit">
                            {saving ? "Sending…" : "Initiate"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </section>
    );
}
